import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  LayoutTemplate,
  Bot,
  BookOpen,
  MessageSquarePlus,
  ChevronRight,
  FileSignature,
  Users,
  Activity,
  Mail,
  SquarePen,
  Wallet,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";

type NavItem = {
  to: string;
  label: string;
  icon: JSX.Element;
  tourKey?: string;
};

export default function MobileTabBar() {
  const { user, isLoading } = useAuth();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isHintDismissed, setIsHintDismissed] = useState(false);
  const isAdmin = user?.role === "admin";
  const navItems: NavItem[] = user
    ? isAdmin
      ? [
          {
            to: "/dashboard",
            label: "Dashboard",
            icon: <LayoutDashboard className="h-4 w-4" />,
            tourKey: "home",
          },
          {
            to: "/cv-builder",
            label: "Builder",
            icon: <FileText className="h-4 w-4" />,
            tourKey: "builder",
          },
          {
            to: "/my-resumes",
            label: "Resumes",
            icon: <FolderOpen className="h-4 w-4" />,
            tourKey: "resumes",
          },
          {
            to: "/templates",
            label: "Templates",
            icon: <LayoutTemplate className="h-4 w-4" />,
            tourKey: "templates",
          },
          {
            to: "/blog",
            label: "Blogs",
            icon: <BookOpen className="h-4 w-4" />,
            tourKey: "blog",
          },
          {
            to: "/admin/feedback",
            label: "Feedback",
            icon: <MessageSquarePlus className="h-4 w-4" />,
            tourKey: "feedback",
          },
          {
            to: "/admin/finance",
            label: "Finance",
            icon: <Wallet className="h-4 w-4" />,
          },
          {
            to: "/admin/user-cvs",
            label: "User CVs",
            icon: <Users className="h-4 w-4" />,
          },
          {
            to: "/admin/newsletter",
            label: "Newsletter",
            icon: <Mail className="h-4 w-4" />,
          },
          {
            to: "/admin/blog/new",
            label: "New Post",
            icon: <SquarePen className="h-4 w-4" />,
          },
          {
            to: "/ai-assistant",
            label: "AI",
            icon: <Bot className="h-4 w-4" />,
            tourKey: "ai",
          },
          {
            to: "/admin/ai-logs",
            label: "AI Logs",
            icon: <Activity className="h-4 w-4" />,
          },
          {
            to: "/cover-letter",
            label: "Cover Letter",
            icon: <FileSignature className="h-4 w-4" />,
            tourKey: "cover-letter",
          },
        ]
      : [
          {
            to: "/home",
            label: "Home",
            icon: <LayoutDashboard className="h-4 w-4" />,
            tourKey: "home",
          },
          {
            to: "/cv-builder",
            label: "Builder",
            icon: <FileText className="h-4 w-4" />,
            tourKey: "builder",
          },
          {
            to: "/my-resumes",
            label: "Resumes",
            icon: <FolderOpen className="h-4 w-4" />,
            tourKey: "resumes",
          },
          {
            to: "/templates",
            label: "Templates",
            icon: <LayoutTemplate className="h-4 w-4" />,
            tourKey: "templates",
          },
          {
            to: "/blog",
            label: "Blogs",
            icon: <BookOpen className="h-4 w-4" />,
            tourKey: "blog",
          },
          {
            to: "/feedback",
            label: "Feedback",
            icon: <MessageSquarePlus className="h-4 w-4" />,
            tourKey: "feedback",
          },
          {
            to: "/ai-assistant",
            label: "AI",
            icon: <Bot className="h-4 w-4" />,
            tourKey: "ai",
          },
          {
            to: "/cover-letter",
            label: "Cover Letter",
            icon: <FileSignature className="h-4 w-4" />,
            tourKey: "cover-letter",
          },
        ]
    : [];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(maxScrollLeft - el.scrollLeft > 4);
    };

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [navItems.length]);

  useEffect(() => {
    if (!canScrollRight || hasInteracted) return;
    const timerId = window.setTimeout(() => {
      setIsHintDismissed(true);
    }, 7000);

    return () => window.clearTimeout(timerId);
  }, [canScrollRight, hasInteracted]);

  const handleUserScrollIntent = () => {
    setHasInteracted(true);
  };

  const showSwipeHint =
    navItems.length > 5 && canScrollRight && !hasInteracted && !isHintDismissed;

  if (isLoading || !user) {
    return null;
  }

  return (
    <nav className="fixed bottom-4 left-1/2 z-40 w-[calc(100%-1rem)] max-w-md -translate-x-1/2 md:hidden">
      <div className="rounded-3xl border border-white/60 bg-white/55 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 shadow-2xl backdrop-blur-xl">
        <div className="relative">
          <div
            ref={scrollRef}
            className="mobile-tab-scroll overflow-x-auto rounded-2xl bg-white/35 p-1"
            onPointerDown={handleUserScrollIntent}
            onTouchStart={handleUserScrollIntent}
          >
            <div className="flex min-w-full snap-x snap-mandatory">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-tour-mobile={item.tourKey}
                  className={({ isActive }) =>
                    `snap-start flex shrink-0 basis-1/5 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[11px] font-semibold transition ${
                      isActive
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-gray-600 hover:bg-white/60 hover:text-gray-800"
                    }`
                  }
                >
                  {item.icon}
                  <span className="w-full truncate text-center leading-tight">
                    {item.label}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>

          <div
            className={`pointer-events-none absolute inset-y-1 left-1 w-5 rounded-l-2xl bg-gradient-to-r from-white/85 to-transparent transition-opacity ${
              canScrollLeft ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`pointer-events-none absolute inset-y-1 right-1 w-8 rounded-r-2xl bg-gradient-to-l from-white/90 to-transparent transition-opacity ${
              canScrollRight ? "opacity-100" : "opacity-0"
            } ${showSwipeHint ? "tab-edge-glow" : ""}`}
          />

          <div
            className={`pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full border border-indigo-100 bg-white/90 px-2 py-1 text-[10px] font-semibold text-indigo-700 shadow-sm transition-opacity duration-300 ${
              showSwipeHint ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="tab-hint-pill">More</span>
            <ChevronRight className="tab-hint-icon h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    </nav>
  );
}
